import {
  createReactRouterV6Options,
  ReactIntegration
} from "@grafana/faro-react";
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType
} from "react-router-dom";

import {
  FetchTransport,
  TransportItem,
  APIEvent,
  ConsoleInstrumentation,
  ConsoleTransport,
  ErrorsInstrumentation,
  initializeFaro,
  getWebInstrumentations,
  SessionInstrumentation,
  WebVitalsInstrumentation
} from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { useEffect } from "react";
import eventEmitter from "../../eventEmitter";
import { getCookie } from "../../utils/cookies";
import { getConfig } from "../../config/config";
import { getActiveClickContext } from "../../utils/traceContextStore";

const appVersion = process.env.REACT_APP_VERSION || "1.0.0"; // Default if not set

// Custom Batching Transport extending FetchTransport
class BatchingTransport extends FetchTransport {
  private queue: TransportItem<APIEvent>[] = [];

  private batchInterval = 5000; // Send logs every 5 seconds

  private maxQueueSize = 50; // Send logs immediately if queue exceeds 10 logs

  constructor(url: string, apiKey: string) {
    super({ url, apiKey }); // Call the constructor of FetchTransport with config object

    // Start the interval to send logs
    setInterval(() => this.sendLogs(), this.batchInterval);
  }

  send(items: TransportItem<APIEvent>[]): Promise<void> {
    // Add all items to the queue
    this.queue.push(...items);

    // If the queue exceeds max size, send immediately
    if (this.queue.length >= this.maxQueueSize) {
      this.sendLogs();
    }

    // Since the original send method is asynchronous, return a resolved Promise
    return Promise.resolve();
  }

  private sendLogs() {
    if (this.queue.length === 0) {
      return;
    }

    const logsToSend = this.queue.splice(0, this.queue.length);

    // Use the parent class's send method to send the batched logs
    super.send(logsToSend).catch((err) => {
      console.error("Failed to send logs:", err);
      // Re-queue the logs on failure
      this.queue.unshift(...logsToSend);
    });
  }
}

const initialize = (
  url: string,
  apiKey: string,
  email: string,
  userId: string,
  apiUrl: string
): (() => void) | undefined => {
  if (!apiKey || !url || !email || !userId || (window as any).faroInstance)
    return;

  console.log("Initializing Faro with:", { apiKey, url, email, userId });

  const faro = initializeFaro({
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: [new RegExp(`${apiUrl}.*`)]
        }
      }),
      new ErrorsInstrumentation(),
      new WebVitalsInstrumentation(),
      new ConsoleInstrumentation({ disabledLevels: [] }), // Capture console.log
      new SessionInstrumentation(),
      new ReactIntegration({
        router: createReactRouterV6Options({
          createRoutesFromChildren,
          matchRoutes,
          Routes,
          useLocation,
          useNavigationType
        })
      })
    ],
    transports: [
      new BatchingTransport(url, apiKey), // Use the custom batching transport
      new ConsoleTransport() // Still log to the console
    ],
    app: {
      name: "tc-web-app-service",
      version: appVersion
    }
  });

  faro.api.setUser({
    email,
    id: userId
  });

  // Capture trace context only for XHRs to the main backend API
  const xhrUrlMap = new WeakMap<XMLHttpRequest, string>();

  const _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method: string, url: string | URL, ...rest: any[]) {
    xhrUrlMap.set(this, String(url));
    return (_open as any).apply(this, [method, url, ...rest]);
  };

  // Override the traceparent OTel injects for API XHRs with the click's traceId.
  // This groups all backend spans for a click under one traceId in Tempo.
  const _setRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
  XMLHttpRequest.prototype.setRequestHeader = function(name: string, value: string) {
    if (String(name).toLowerCase() === 'traceparent' && (xhrUrlMap.get(this) ?? '').includes(apiUrl)) {
      const clickCtx = getActiveClickContext();
      if (clickCtx) {
        value = `00-${clickCtx.traceId}-${clickCtx.spanId}-01`;
      }
    }
    return _setRequestHeader.call(this, name, value);
  };

  (window as any).faroInstance = faro;

  return () => {
    XMLHttpRequest.prototype.open = _open;
    XMLHttpRequest.prototype.setRequestHeader = _setRequestHeader;
  };
};

const useFaroInitializer = () => {
  useEffect(() => {
    const email = getCookie("email") as string;
    const personId = getCookie("person_id") as string;
    const config = getConfig();

    // Initial Faro initialization
    const cleanup = initialize(config.faroUrl, config.faroAPIKey, email, personId, config.apiUrl);

    const handleUserLogin = () => {
      const newEmail = getCookie("email") as string;
      const newPersonId = getCookie("person_id") as string;
      const newConfig = getConfig();
      initialize(
        newConfig.faroUrl,
        newConfig.faroAPIKey,
        newEmail,
        newPersonId,
        newConfig.apiUrl
      );
    };

    eventEmitter.addEventListener("userLogin", handleUserLogin);

    return () => {
      eventEmitter.removeEventListener("userLogin", handleUserLogin);
      cleanup?.();
    };
  }, []); // Empty dependency array since the initialization relies on fresh values on each render
};

export default useFaroInitializer;

